import { Injectable, signal, inject, computed } from '@angular/core';
import { NavigationSection } from '../models/navigation.interface';
import { User } from '../models/user.interface';
import { AuthService } from '../../auth/services/auth.service';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    private readonly authService = inject(AuthService);
    private unleashService = inject(UnleashService)
    // Sidebar state
    sidebarCollapsed = signal(false);

    // User data - initialized with default, updated from Keycloak
    currentUser = signal<User>({
        id: '1',
        name: 'User',
        email: '',
        initials: 'U'
    });

    // Feature flag: Check if user is a FilterLabs employee (has @filterlabs.ai email)
    isFilterLabsUser = computed(() => {
        const email = this.currentUser().email;
        if (!email) return false;
        const domain = email.split('@')[1]?.toLowerCase();
        return domain === 'filterlabs.ai';
    });

    // Navigation structure
    navigationSections = signal<NavigationSection[]>([
        {
            title: 'WORKSPACE',
            items: [
                {
                    id: 'create',
                    label: 'Create Pipeline',
                    icon: 'heroPlusCircle',
                    route: '/search',
                    isActive: false
                },
                {
                    id: 'pipelines',
                    label: 'Pipelines',
                    icon: 'heroRss',
                    route: '/pipelines',
                    isActive: false,
                }
            ]
        }]);

    constructor() {
        // Subscribe to auth profile changes to update user info from Keycloak
        this.authService.getUserProfile().subscribe(profile => {
            if (profile) {
                const firstName = profile.firstName || '';
                const lastName = profile.lastName || '';
                const username = profile.username || '';
                const email = profile.email || '';

                // Build full name from available fields
                const fullName = `${firstName} ${lastName}`.trim() || username || 'User';
                const initials = this.getInitials(firstName, lastName, username);

                this.unleashService.unleash.updateContext({userId: email});

                this.currentUser.set({
                    id: profile.id || '1',
                    name: fullName,
                    email: email,
                    initials: initials
                });
            }
        });
    }

    /**
     * Generate initials from user name fields
     */
    private getInitials(firstName: string, lastName: string, username?: string): string {
        if (firstName && lastName) {
            return `${firstName[0]}${lastName[0]}`.toUpperCase();
        }
        if (firstName && firstName.length >= 2) {
            return firstName.substring(0, 2).toUpperCase();
        }
        if (username && username.length >= 2) {
            return username.substring(0, 2).toUpperCase();
        }
        return 'U';
    }

    toggleSidebar(): void {
        this.sidebarCollapsed.update(collapsed => !collapsed);
    }

    setActiveNavigation(itemId: string): void {
        this.navigationSections.update(sections =>
            sections.map(section => ({
                ...section,
                items: section.items.map(item => ({
                    ...item,
                    isActive: item.id === itemId
                }))
            }))
        );
    }

    checkUploadEnabled(): boolean {
      return this.unleashService.isEnabled('upload_enabled')
    }
}
